<template>
  <div class="container text-secondary">
    <div class="row mb-3">
      <div class="col-2"/>
      <div class="col">
        <div class="h4">Hartenjagen</div>
      </div>
      <div class="col-2 text-end pe-2">
        <img src="@/assets/back-48.png" alt="terug" class="img-fluid"
             style="height:32px; cursor:pointer;" @click="$router.push('/')"/>
      </div>
    </div>

    <hr class="divider">

    <!-- Setup scherm -->
    <div v-if="!gameStarted">
      <div class="card-modern">
        <div class="row mb-3">
          <div class="col-5 text-end fw-bold pt-1">Kaartdek:</div>
          <div class="col text-start">
            <button v-for="size in [32, 52]" :key="size"
                    class="btn btn-sm me-2"
                    :class="cardDeck === size ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="cardDeck = size">
              {{ size }} kaarten
            </button>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-5 text-end fw-bold pt-1">Grens:</div>
          <div class="col text-start">
            <button v-for="g in [25, 50]" :key="g"
                    class="btn btn-sm me-2"
                    :class="grens === g ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="grens = g">
              {{ g }} punten
            </button>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-5 text-end fw-bold pt-1">Spelvariant:</div>
          <div class="col text-start">
            <div class="form-check mb-2">
              <input class="form-check-input" type="radio" id="variantOns" value="ons" v-model="variant"/>
              <label class="form-check-label" for="variantOns">
                Onze variant<br>
                <small class="text-muted">♥ = 1pt &nbsp;·&nbsp; ♣J = 2pt &nbsp;·&nbsp; ♠Q = 5pt</small>
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" id="variantKlassiek" value="klassiek" v-model="variant"/>
              <label class="form-check-label" for="variantKlassiek">
                Klassiek<br>
                <small class="text-muted">♥ = 1pt &nbsp;·&nbsp; ♠Q = 13pt</small>
              </label>
            </div>
          </div>
        </div>

        <hr class="divider">

        <div class="row">
          <div class="col text-muted" style="font-size:0.85rem; line-height:1.6;">
            <strong>Fase 1 – Brengen:</strong> optellen tot iemand ≥ {{ grens }} punten.<br>
            <strong>Fase 2 – Halen:</strong> aftrekken tot iemand op 0 staat. Onder 0 = stuiteren.<br>
            <strong>Pit:</strong> alle punten bij één speler → anderen +10 (fase 1) of keuze A/B (fase 2).<br>
            Max per ronde: <strong>{{ maxScore }} punten</strong>
          </div>
        </div>
      </div>

      <div class="text-center">
        <button class="btn-primary-modern" @click="startGame">Begin</button>
      </div>
    </div>

    <!-- Spel bezig -->
    <div v-else>

      <!-- Fase indicator -->
      <div class="card-modern text-center mb-3"
           :style="fase === 'brengen' ? 'border-left:4px solid #667eea;' : 'border-left:4px solid #e53e3e;'">
        <div style="font-size:1rem; font-weight:800;">
          {{ fase === 'brengen' ? '📈 Fase 1 – Brengen' : '📉 Fase 2 – Halen' }}
        </div>
        <div style="font-size:0.85rem; color:#718096;">
          {{ fase === 'brengen' ? `Punten optellen · grens: ${grens}` : 'Punten aftrekken · doel: 0' }}
        </div>
      </div>

      <!-- Pit keuze (alleen fase 2) -->
      <div v-if="pitKeuzeNodig" class="card-modern" style="border-left:4px solid #ed8936;">
        <div class="text-center mb-3">
          <div style="font-size:1.3rem;">🎯 Pit!</div>
          <div style="font-weight:700; color:#4a5568;">
            {{ players[pitSpelerIndex].name }} heeft alle punten gehaald
          </div>
          <div style="font-size:0.85rem; color:#a0aec0; margin-top:4px;">Kies een optie:</div>
        </div>
        <div class="row g-2">
          <div class="col-6">
            <div class="pit-optie" @click="verwerkPitKeuze('A')">
              <div style="font-weight:800; color:#667eea; font-size:1.1rem;">Optie A</div>
              <div style="font-size:0.85rem; color:#4a5568; margin-top:4px;">
                <strong>{{ players[pitSpelerIndex].name }}</strong> − {{ maxScore }} punten<br>
                <span style="color:#a0aec0;">Rest blijft staan</span>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="pit-optie" @click="verwerkPitKeuze('B')">
              <div style="font-weight:800; color:#667eea; font-size:1.1rem;">Optie B</div>
              <div style="font-size:0.85rem; color:#4a5568; margin-top:4px;">
                <strong>{{ players[pitSpelerIndex].name }}</strong> blijft staan<br>
                <span style="color:#a0aec0;">Rest + 10 punten</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Totaal fout melding -->
      <div v-else-if="totaalFout" class="card-modern" style="border-left:4px solid #e53e3e;">
        <div class="text-center mb-3">
          <div style="font-size:1.3rem;">⚠️ Totaal klopt niet!</div>
          <div style="font-weight:700; color:#4a5568; margin-top:8px;">
            De scores tellen op tot
            <strong style="color:#e53e3e;">{{ Object.values(rondeScores).reduce((s,v) => s+v, 0) }}</strong>
            maar moet <strong style="color:#667eea;">{{ maxScore }}</strong> zijn.
          </div>
          <div style="font-size:0.85rem; color:#a0aec0; margin-top:4px;">
            Controleer de scores en probeer opnieuw.
          </div>
        </div>
        <div class="row g-2">
          <div class="col-6">
            <button class="btn-secondary-modern" style="width:100%;" @click="herstelInvoer">
              Opnieuw invoeren
            </button>
          </div>
          <div class="col-6">
            <button class="btn-primary-modern" style="width:100%;"
                    @click="totaalFout = false; scoringStarted = false; verwerkRonde()">
              Toch opslaan
            </button>
          </div>
        </div>
      </div>

      <!-- Score invoer -->
      <div v-else-if="scoringStarted" class="card-modern">
        <div class="dealer-bar mb-3">
          <span>🃏 Deler: <strong>{{ players[dealerIndex].name }}</strong></span>
          <span style="color:#a0aec0; font-size:0.85rem;">Ronde {{ currentRound }}</span>
        </div>

        <div class="text-center mb-3">
          <div style="font-weight:700; color:#4a5568;">
            {{ players[currentPlayer].gender === 'female' ? '👩' : '👨' }}
            Punten van <strong>{{ players[currentPlayer].name }}</strong>
          </div>
          <div style="font-size:0.85rem; color:#a0aec0; margin-top:2px;">
            Huidig totaal: <strong>{{ totalen[currentPlayer] }}</strong>
          </div>
          <div v-if="isLaatsteSpeler" style="font-size:0.8rem; color:#667eea; margin-top:4px;">
            ✨ Automatisch berekend — controleer en bevestig
          </div>
        </div>

        <div style="background:#f7fafc; border-radius:12px; padding:12px; text-align:center;
          margin-bottom:12px; font-size:2rem; font-weight:800; color:#2d3748;
          min-height:56px; letter-spacing:4px;">
          {{ invoer || '0' }}
        </div>

        <div class="cijfer-grid mb-2">
          <button v-for="n in [1,2,3,4,5,6,7,8,9]" :key="n"
                  class="cijfer-btn" @click="invoerCijfer(String(n))">{{ n }}</button>
          <button class="cijfer-btn" @click="invoerCijfer('0')">0</button>
          <button class="cijfer-btn delete-btn" @click="verwijderCijfer">⌫</button>
        </div>

        <div style="font-size:0.8rem; color:#a0aec0; text-align:center; margin-bottom:12px;">
          max {{ maxScore }} punten per ronde
          · ingevoerd: {{ Object.values(rondeScores).reduce((s,v) => s+v, 0) + (parseInt(invoer) || 0) }}/{{ maxScore }}
        </div>

        <button class="btn-primary-modern" style="width:100%;" @click="confirmScore">
          Bevestig
        </button>
      </div>

      <!-- Wacht op invoer / winnaar -->
      <div v-else class="card-modern text-center">
        <div v-if="winnaar !== null">
          <div style="font-size:2rem;">🏆</div>
          <div class="h5 mt-1">{{ players[winnaar].name }} wint!</div>
        </div>
        <div v-else>
          <div class="dealer-bar mb-3">
            <span>🃏 Deler: <strong>{{ players[dealerIndex].name }}</strong></span>
            <span style="color:#a0aec0; font-size:0.85rem;">Ronde {{ currentRound }}</span>
          </div>
          <button class="btn-primary-modern" @click="startScoring">Scores invoeren</button>
        </div>
      </div>

      <!-- Scorebord -->
      <div class="card-modern">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="h6 mb-0">Scorebord</div>
          <button class="edit-toggle" @click="editModus = !editModus">
            <img src="@/assets/edit-48.png" alt="bewerken" style="height:22px;"/>
            {{ editModus ? 'Klaar' : 'Wijzig' }}
          </button>
        </div>

        <div v-if="editModus" style="font-size:0.8rem; color:#a0aec0; margin-bottom:8px;">
          Tik op een score om te wijzigen
        </div>

        <div class="scorebord-wrapper">
          <table class="sticky-table">
            <thead>
            <tr>
              <th class="col-naam sticky-links">Speler</th>
              <th v-for="r in currentRound - 1" :key="r" class="col-ronde">R{{ r }}</th>
              <th class="col-totaal sticky-rechts">Tot</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(player, pi) in players" :key="player.id"
                :class="{ 'winner-row': winnaar === pi }">
              <td class="col-naam sticky-links">
                {{ player.name }}
                <span v-if="winnaar === pi">🏆</span>
              </td>
              <td v-for="r in currentRound - 1" :key="r"
                  class="col-ronde"
                  :class="{ 'edit-cel': editModus }"
                  @click="editModus ? openEdit(pi, r-1) : null">
                {{ scores[pi]?.[r - 1] ?? '' }}
              </td>
              <td class="col-totaal sticky-rechts">{{ totalen[pi] }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit popup -->
    <div v-if="editPopup" class="popup-overlay" @click.self="editPopup = false">
      <div class="popup-box">
        <div style="font-weight:700; color:#4a5568; margin-bottom:12px; text-align:center;">
          Score wijzigen: <strong>{{ players[editSpeler].name }}</strong> · R{{ editRonde + 1 }}
        </div>

        <div style="background:#f7fafc; border-radius:12px; padding:12px; text-align:center;
          margin-bottom:12px; font-size:2rem; font-weight:800; color:#2d3748;
          min-height:56px; letter-spacing:4px;">
          {{ editInvoer || '0' }}
        </div>

        <div class="cijfer-grid mb-2">
          <button v-for="n in [1,2,3,4,5,6,7,8,9]" :key="n"
                  class="cijfer-btn" @click="editInvoerCijfer(String(n))">{{ n }}</button>
          <button class="cijfer-btn" @click="editInvoerCijfer('0')">0</button>
          <button class="cijfer-btn delete-btn" @click="editInvoer = editInvoer.slice(0,-1)">⌫</button>
        </div>

        <button class="btn-primary-modern" style="width:100%; margin-bottom:8px;" @click="slaEditOp">
          Opslaan
        </button>
        <button class="btn-secondary-modern" style="width:100%;" @click="editPopup = false">
          Annuleren
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/store";

export default {
  name: "HartenjagenGame",
  data() {
    return {
      gameStarted: false,
      scoringStarted: false,
      currentRound: 1,
      currentPlayer: 0,
      invoer: '',
      scores: [],
      totalen: [],
      rondeScores: {},
      grens: 50,
      cardDeck: 32,
      variant: 'ons',
      fase: 'brengen',
      winnaar: null,
      pitKeuzeNodig: false,
      pitSpelerIndex: null,
      dealerIndex: 0,
      totaalFout: false,
      editModus: false,
      editPopup: false,
      editSpeler: null,
      editRonde: null,
      editInvoer: '',
    };
  },
  computed: {
    players() {
      return store.state.playersRoom.length >= 2
          ? store.state.playersRoom
          : store.state.players;
    },
    maxScore() {
      if (this.variant === 'klassiek') return 26;
      const harten = this.cardDeck === 32 ? 8 : 13;
      return harten + 2 + 5;
    },
    isLaatsteSpeler() {
      return Object.keys(this.rondeScores).length === this.players.length - 1;
    },
  },
  methods: {
    startGame() {
      this.scores = this.players.map(() => []);
      this.totalen = this.players.map(() => 0);
      this.rondeScores = {};
      this.currentRound = 1;
      this.fase = 'brengen';
      this.winnaar = null;
      this.pitKeuzeNodig = false;
      this.pitSpelerIndex = null;
      this.dealerIndex = 0;
      this.totaalFout = false;
      this.currentPlayer = (this.dealerIndex + 1) % this.players.length;
      this.gameStarted = true;
    },
    startScoring() {
      this.currentPlayer = (this.dealerIndex + 1) % this.players.length;
      this.invoer = '';
      this.rondeScores = {};
      this.totaalFout = false;
      this.scoringStarted = true;
    },
    herstelInvoer() {
      this.rondeScores = {};
      this.totaalFout = false;
      this.startScoring();
    },
    invoerCijfer(n) {
      if (this.invoer === '' && n === '0') return;
      const nieuw = this.invoer + n;
      if (parseInt(nieuw) > this.maxScore) return;
      this.invoer = nieuw;
    },
    verwijderCijfer() {
      this.invoer = this.invoer.slice(0, -1);
    },
    berekenLaatste() {
      const som = Object.values(this.rondeScores).reduce((s, v) => s + v, 0);
      const laatste = this.maxScore - som;
      if (laatste >= 0) {
        this.invoer = String(laatste);
      }
    },
    confirmScore() {
      const score = parseInt(this.invoer) || 0;
      this.rondeScores[this.currentPlayer] = score;
      this.invoer = '';

      const startPlayer = (this.dealerIndex + 1) % this.players.length;
      const gespeeld = Object.keys(this.rondeScores).length;

      if (gespeeld < this.players.length) {
        this.currentPlayer = (startPlayer + gespeeld) % this.players.length;
        if (gespeeld === this.players.length - 1) {
          this.berekenLaatste();
        }
      } else {
        const som = Object.values(this.rondeScores).reduce((s, v) => s + v, 0);
        if (som !== this.maxScore) {
          this.totaalFout = true;
          this.scoringStarted = false;
        } else {
          this.totaalFout = false;
          this.scoringStarted = false;
          this.verwerkRonde();
        }
      }
    },
    verwerkRonde() {
      const rondeArray = this.players.map((_, pi) => this.rondeScores[pi] ?? 0);

      const pitIndex = rondeArray.findIndex(s => s === this.maxScore);
      const isPit = pitIndex !== -1 && rondeArray.every((s, i) =>
          i === pitIndex ? s === this.maxScore : s === 0);

      if (isPit && this.fase === 'halen') {
        this.pitSpelerIndex = pitIndex;
        this.pitKeuzeNodig = true;
        rondeArray.forEach((s, pi) => { this.scores[pi].push(s); });
        return;
      }

      if (isPit && this.fase === 'brengen') {
        rondeArray.forEach((s, pi) => {
          this.scores[pi].push(s);
          this.totalen[pi] += pi === pitIndex ? this.maxScore : 10;
        });
      } else {
        rondeArray.forEach((s, pi) => {
          this.scores[pi].push(s);
          if (this.fase === 'brengen') {
            this.totalen[pi] += s;
          } else {
            let nieuw = this.totalen[pi] - s;
            if (nieuw < 0) nieuw = Math.abs(nieuw);
            this.totalen[pi] = nieuw;
          }
        });
      }

      this.afrondRonde();
    },
    verwerkPitKeuze(keuze) {
      const pi = this.pitSpelerIndex;
      if (keuze === 'A') {
        let nieuw = this.totalen[pi] - this.maxScore;
        if (nieuw < 0) nieuw = Math.abs(nieuw);
        this.totalen[pi] = nieuw;
      } else {
        this.totalen.forEach((_, i) => { if (i !== pi) this.totalen[i] += 10; });
      }
      this.pitKeuzeNodig = false;
      this.pitSpelerIndex = null;
      this.afrondRonde();
    },
    afrondRonde() {
      this.currentPlayer = 0;
      this.currentRound++;
      this.dealerIndex = (this.dealerIndex + 1) % this.players.length;

      if (this.fase === 'halen') {
        const winIdx = this.totalen.findIndex(t => t === 0);
        if (winIdx !== -1) { this.winnaar = winIdx; return; }
      }
      if (this.fase === 'brengen' && this.totalen.some(t => t >= this.grens)) {
        this.fase = 'halen';
      }
    },
    openEdit(pi, rondeIndex) {
      this.editSpeler = pi;
      this.editRonde = rondeIndex;
      this.editInvoer = String(this.scores[pi]?.[rondeIndex] ?? '');
      this.editPopup = true;
    },
    editInvoerCijfer(n) {
      if (this.editInvoer === '' && n === '0') return;
      const nieuw = this.editInvoer + n;
      if (parseInt(nieuw) > this.maxScore) return;
      this.editInvoer = nieuw;
    },
    slaEditOp() {
      const score = parseInt(this.editInvoer) || 0;
      this.scores[this.editSpeler][this.editRonde] = score;
      this.totalen = this.players.map((_, pi) => {
        return this.scores[pi].reduce((sum, s) => sum + (s ?? 0), 0);
      });
      this.editPopup = false;
    },
  },
}
</script>

<style scoped>
.dealer-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f0f4ff;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.9rem;
  color: #4a5568;
}

.scorebord-wrapper {
  overflow-x: auto;
  border-radius: 8px;
}

.sticky-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
}

.sticky-table th,
.sticky-table td {
  padding: 6px 8px;
  text-align: center;
  border-bottom: 1px solid #f0f4f8;
  white-space: nowrap;
  background: white;
}

.sticky-table thead th {
  color: #718096;
  font-weight: 700;
  border-bottom: 2px solid #e2e8f0;
}

.sticky-table tr:last-child td {
  border-bottom: none;
}

.col-naam {
  text-align: left !important;
  font-weight: 600;
  color: #2d3748;
  min-width: 80px;
}

.sticky-links {
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 2px 0 4px rgba(0,0,0,0.06);
}

.col-totaal {
  font-weight: 800 !important;
  color: #667eea !important;
  min-width: 44px;
}

.sticky-rechts {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -2px 0 4px rgba(0,0,0,0.06);
}

.col-ronde {
  min-width: 36px;
}

.winner-row td {
  background: #fffff0 !important;
  font-weight: 700;
}

.edit-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.15s;
}
.edit-toggle:hover { border-color: #667eea; color: #667eea; }
.edit-cel { cursor: pointer; }
.edit-cel:hover { background: #ebf4ff !important; border-radius: 4px; }

.cijfer-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.cijfer-btn {
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  font-size: 1.3rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}
.cijfer-btn:hover { border-color: #667eea; background: #ebf4ff; color: #667eea; }
.delete-btn { color: #e53e3e; }
.delete-btn:hover { border-color: #e53e3e; background: #fff5f5; color: #e53e3e; }

.pit-optie {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.15s;
  height: 100%;
}
.pit-optie:hover { border-color: #667eea; background: #ebf4ff; }

.btn-secondary-modern {
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 12px;
  padding: 10px 24px;
  font-size: 1rem;
  cursor: pointer;
}
.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.popup-box {
  background: white;
  border-radius: 16px;
  padding: 20px;
  width: 90%;
  max-width: 360px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
}
</style>